import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { format } from "date-fns";

import { Webhook, WebhookDelivery, WebhookDeliveryStatus } from "./Webhook";
import { fetchDeliveries, PaginatedDeliveries, redeliver, sendTestEvent } from "./webhooksApi";
import NoResultsFound from "../../common/components/noResultsFound/NoResultsFound";

interface WebhookDeliveriesDialogProps {
  webhook?: Webhook;
  visible: boolean;
  onClose: () => void;
}

const STATUS_FILTER_OPTIONS: Array<{ label: string; value: WebhookDeliveryStatus | null }> = [
  { label: "Toate", value: null },
  { label: "In asteptare", value: "Pending" },
  { label: "Reusit", value: "Succeeded" },
  { label: "Esuat", value: "Failed" },
  { label: "Esuat definitiv", value: "FailedTerminal" },
];

const DELIVERY_STATUS_SEVERITY: Record<WebhookDeliveryStatus, "success" | "warning" | "danger" | "info"> = {
  Pending: "info",
  Succeeded: "success",
  Failed: "warning",
  FailedTerminal: "danger",
};

const DELIVERY_STATUS_LABEL: Record<WebhookDeliveryStatus, string> = {
  Pending: "In asteptare",
  Succeeded: "Reusit",
  Failed: "Esuat",
  FailedTerminal: "Esuat definitiv",
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const DEFAULT_PAGE_SIZE = 20;
const TEST_AUTO_REFRESH_DELAY_MS = 1500;

const truncate = (value: string | undefined, max: number) => {
  if (!value) return "—";
  return value.length <= max ? value : `${value.substring(0, max)}…`;
};

export default function WebhookDeliveriesDialog({ webhook, visible, onClose }: WebhookDeliveriesDialogProps) {
  const [data, setData] = useState<PaginatedDeliveries | null>(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(DEFAULT_PAGE_SIZE);
  const [statusFilter, setStatusFilter] = useState<WebhookDeliveryStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [redeliveringId, setRedeliveringId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (visible && webhook) {
      setFirst(0);
      setRows(DEFAULT_PAGE_SIZE);
      setStatusFilter(null);
      load(webhook.id, 1, DEFAULT_PAGE_SIZE, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, webhook?.id]);

  const load = async (webhookId: string, pageNumber: number, pageSize: number, status: WebhookDeliveryStatus | null) => {
    setIsLoading(true);
    try {
      const result = await fetchDeliveries(webhookId, pageNumber, pageSize, status ?? undefined);
      setData(result);
    } finally {
      setIsLoading(false);
    }
  };

  const onPage = (event: DataTableStateEvent) => {
    if (!webhook) return;
    setFirst(event.first);
    setRows(event.rows);
    const pageNumber = Math.floor(event.first / event.rows) + 1;
    load(webhook.id, pageNumber, event.rows, statusFilter);
  };

  const onStatusChange = (status: WebhookDeliveryStatus | null) => {
    if (!webhook) return;
    setStatusFilter(status);
    setFirst(0);
    load(webhook.id, 1, rows, status);
  };

  const refresh = () => {
    if (!webhook) return;
    const pageNumber = Math.floor(first / rows) + 1;
    load(webhook.id, pageNumber, rows, statusFilter);
  };

  const onSendTest = async () => {
    if (!webhook) return;
    setIsSendingTest(true);
    try {
      await sendTestEvent(webhook.id);
      // Give Hangfire a moment to pick the job up before refreshing.
      setTimeout(() => refresh(), TEST_AUTO_REFRESH_DELAY_MS);
    } finally {
      setIsSendingTest(false);
    }
  };

  const onRedeliver = async (delivery: WebhookDelivery) => {
    setRedeliveringId(delivery.id);
    try {
      await redeliver(delivery.id);
      setTimeout(() => refresh(), TEST_AUTO_REFRESH_DELAY_MS);
    } finally {
      setRedeliveringId(undefined);
    }
  };

  return (
    <Dialog
      className="w-almost-full md:w-9 lg:w-9"
      header={`Livrari${webhook ? ` — ${webhook.url}` : ""}`}
      visible={visible}
      onHide={onClose}
    >
      <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <Dropdown
          className="w-12rem"
          value={statusFilter}
          options={STATUS_FILTER_OPTIONS}
          onChange={(e) => onStatusChange(e.value)}
          placeholder="Toate"
        />
        <div className="flex align-items-center gap-2">
          <Button
            label="Trimite test"
            icon="pi pi-send"
            severity="info"
            onClick={onSendTest}
            loading={isSendingTest}
            disabled={!webhook?.isEnabled}
            tooltip={!webhook?.isEnabled ? "Webhook-ul este inactiv" : undefined}
            tooltipOptions={{ position: "top" }}
          />
          <Button label="Refresh" icon="pi pi-refresh" outlined onClick={refresh} loading={isLoading} />
        </div>
      </div>

      <DataTable
        size="small"
        lazy
        paginator
        first={first}
        rows={rows}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        totalRecords={data?.totalCount ?? 0}
        onPage={onPage}
        loading={isLoading}
        value={data?.items ?? []}
        emptyMessage={<NoResultsFound />}
      >
        <Column
          className="w-9rem"
          header="Status"
          body={(d: WebhookDelivery) => <Tag severity={DELIVERY_STATUS_SEVERITY[d.status]} value={DELIVERY_STATUS_LABEL[d.status]} />}
        />
        <Column field="eventType" header="Eveniment" body={(d: WebhookDelivery) => <span className="font-mono">{d.eventType}</span>} />
        <Column header="Creat" body={(d: WebhookDelivery) => format(new Date(d.created), "dd.MM.yyyy HH:mm:ss")} />
        <Column className="w-6rem" field="attemptCount" header="Incercari" />
        <Column
          className="w-7rem"
          header="HTTP"
          body={(d: WebhookDelivery) => (d.lastResponseStatusCode != null ? d.lastResponseStatusCode : "—")}
        />
        <Column
          header="Eroare"
          body={(d: WebhookDelivery) => (
            <span title={d.lastError ?? ""} className="text-color-secondary">
              {truncate(d.lastError, 60)}
            </span>
          )}
        />
        <Column
          className="w-10rem"
          body={(d: WebhookDelivery) =>
            d.status === "Failed" || d.status === "FailedTerminal" ? (
              <Button
                size="small"
                label="Reincearca"
                icon="pi pi-replay"
                onClick={() => onRedeliver(d)}
                loading={redeliveringId === d.id}
              />
            ) : null
          }
        />
      </DataTable>
    </Dialog>
  );
}
